import * as React from 'react';
import {useEffect, useRef, useState} from "react";
import axios, {AxiosError} from "axios";
import {Token} from "../services/AuthorizationService";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import './Partner.css'
import {Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow} from "@mui/material";
import {AxiosResponse} from "axios";
import * as jose from 'jose'
import ErrorPage from "../error-page";
import {useTranslation} from "react-i18next";
import excel from '../pictures/excel.png';
import es from '../pictures/es.svg';
import en from '../pictures/en.svg';
import logo from '../pictures/logo.svg';
import LogoutIcon from '@mui/icons-material/Logout';
import {format} from "date-fns";

const JWKS = jose.createRemoteJWKSet(new URL(process.env.REACT_APP_AUTH_SERVER_URL + '/oauth2/jwks'))

class TalksDto {
    event: Event
    talks: Talk[]

    constructor(event: Event, talks: Talk[]) {
        this.event = event
        this.talks = talks
    }
}

class Talk {
    talk: string | undefined
    location: string | undefined
    talkCode: string | undefined
    talkStartTime: Date  | undefined
    talkFinishTime: Date  | undefined
    started: boolean  | undefined

    constructor(talk: string, location: string, talkStartTime: Date, talkFinishTime: Date, started: boolean) {
        this.talk = talk
        this.location = location
        this.talkStartTime = talkStartTime
        this.talkFinishTime = talkFinishTime
        this.started = started
    }
}

class Event{
    event: string
    eventStartTime: Date
    eventFinishTime: Date
    isFinished: boolean

    constructor(event: string, eventStartTime: Date, eventFinishTime: Date, isFinished: boolean) {
        this.event = event
        this.eventStartTime = eventStartTime
        this.eventFinishTime = eventFinishTime
        this.isFinished = isFinished
    }
}

class Talks {
    event: Event
    talks: Talk[]

    constructor(event: Event, talks: Talk[]) {
        this.event = event
        this.talks = talks
    }
}

export default function Partner() {

    const { t } = useTranslation()
    const token = new Token(window.sessionStorage.getItem("TOKEN"))
    const [tokenValidated, setTokenValidated] = useState(false)
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const [file, setFile] = useState<File | null>(null)
    const queryClient = useQueryClient()

    const fileInputHandler = () => {
        if (fileInputRef.current?.files?.item(0)){
            let extension = fileInputRef.current?.files?.item(0)?.name.split(".")
            let realExtension = undefined
            if (extension) {
              realExtension = extension[extension.length - 1]
            }
            if (realExtension === "xlsx" || realExtension === "XLSX") {
                const actualFile = fileInputRef.current?.files?.item(0) as File
                setFile(actualFile)
                postTalks(actualFile)
            } else {
                alert(t('partner.onlyExcelAllowed'))
            }
        }
    }

    useEffect(() => {
            verifyToken()
                .then( result => {
                    if (result.payload.roles == 'MODERATOR') {
                        setTokenValidated(true)
                    } else window.location.replace("/")
                })
                .catch(() => {
                    setTokenValidated(false)
                    window.location.replace("/")
                })
        if (token == undefined) {
            setTokenValidated(false)
            window.location.replace("/")
        }
    }, [token])

    async function getTalks(): Promise<TalksDto | null> {
        return axios.get<TalksDto>(process.env.REACT_APP_API_SERVER_URL + "/talks",
            {
                headers: {
                    authorization: "Bearer " + token?.accessCode
                }
            })
            .then(res => {
                return res.data
            })
            .catch((reason: AxiosError) => {
                if (reason.code == "ErrorERR_NETWORK") {
                    return null
                }
                return null
            })
    }

    async function verifyToken() {
        return await jose.jwtVerify(token.accessCode? token.accessCode: "", JWKS, {
            issuer: process.env.REACT_APP_AUTH_SERVER_URL,
            audience: 'client',
        })
    }

    async function postTalks(excelFile: File): Promise<AxiosResponse<string>> {
        const formData = new FormData()
        formData.append("file", excelFile)
        return axios.post(process.env.REACT_APP_API_SERVER_URL + "/talks",
            formData,
            {
                headers: {
                    authorization: "Bearer " + token?.accessCode,
                    "Content-Type": "multipart/form-data"
                }
            })
            .then(res => {
                queryClient.invalidateQueries({ queryKey: ['talks'] })
                return res.data
            })
            .catch(function(error) {
                // Resets the file so the user can retry again
                fileInputRef.current!.value = ""
                if (error.response.status === 403) {
                    alert(t('partner.eventFinishedError'))
                }
                else if (error.response.status === 500) {
                    alert(t('partner.genericError'))
                }
                else if (error.response.data && error.response.data.error && error.response.data.error.startsWith("ERROR_3")) {
                    const reason = error.response.data.error
                    const rowIndexBegining = reason.indexOf("ROW")
                    const rowIndexEnd = rowIndexBegining + "ROW".length
                    const rowNumber = reason.substring(rowIndexEnd, reason.length).trim()
                    alert(t('partner.excelParserError1') + rowNumber + t('partner.excelParserError3'))
                }
                else if (error.response.data && error.response.data.error && error.response.data.error.startsWith("ERROR_4")) {
                    const reason = error.response.data.error
                    const rowIndexBegining = reason.indexOf("ROW")
                    const rowIndexEnd = rowIndexBegining + "ROW".length
                    const cellIndexBegining = reason.indexOf("CELL")
                    const cellIndexEnd = cellIndexBegining + "CELL".length
                    const rowNumber = reason.substring(rowIndexEnd, cellIndexBegining).trim()
                    const cellNumber = reason.substring(cellIndexEnd, reason.length).trim()
                    let message = ""
                    // Cells for dates
                    if (cellNumber === "2" || cellNumber === "4") {
                        message = t('partner.excelDateParserError')
                    }
                    // Cells for hours
                    if (cellNumber === "3" || cellNumber === "5") {
                        message = t('partner.excelTimeParserError')
                    }
                    if (cellNumber === "6") {
                        message = t('partner.excelLocationParserError')
                    }
                    alert(t('partner.excelParserError1') + rowNumber + t('partner.excelParserError2') + message)
                }
            })
    }

    function getRowClassName (index: number) {
        if (index % 2 == 0) {
            return "evenRow"
        }
        return "oddRow"
    }

    function useGetTalks () {
        return useQuery({
            queryKey: ["talks"],
            queryFn: async () => {
                return await getTalks()
            },
            refetchOnWindowFocus: false,
            refetchOnMount: false,
        })
    }

    function getFormatedDate(date: Date): String {
        return format(new Date(date.toString()), "yyyy/MM/dd - HH:mm");
    }

    function logout() {
        window.sessionStorage.removeItem("TOKEN")
        window.sessionStorage.removeItem("CODE_VERIFIER")
        setTokenValidated(false)
    }

    const {
        data: talks,
        isFetching: talksAreFetching
    } = useGetTalks()

    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    if (!talksAreFetching) {
        if (talks && new Date(talks?.event.eventFinishTime) < tomorrow) {
            return <div className={"paddingLeft"}>
                <a href={process.env.REACT_APP_MAIN_URL + ""}><button className={"topRight"} onClick={logout}><LogoutIcon className={"logoutIcon"}/>{t('partner.exit')}</button></a>
                <h1 className={"whiteText textCenter"}>{talks?.event.event}</h1>
                <h2 className={"whiteText textCenter"}>{t('partner.eventFinished1')}</h2>
                <h3 className={"whiteText textCenter"}>{t('partner.eventFinished2')}</h3>
                <img src={logo} alt="ProxyHands logo" className={"logo"}/>
            </div>
        } else if (talks && talks.talks[0].talk !== undefined && talks.talks[0].talk !== null) {
        return <div className={"paddingLeft"}>
            <a href={process.env.REACT_APP_MAIN_URL + ""}><button className={"topRight"} onClick={logout}><LogoutIcon className={"logoutIcon"}/>{t('partner.exit')}</button></a>
            <h1 className={"whiteText textCenter"}>{talks?.event.event}</h1>
        <TableContainer component={Paper} className={"table"}>
            <Table sx={{ minWidth: 650 }} aria-label="caption table">
                <caption className={"whiteText"}>{t('partner.captionTable')}</caption>
                <TableHead>
                    <TableRow>
                        <TableCell className={"talkHeader whiteText boldText"}>{t('partner.tableNameHeader')}</TableCell>
                        <TableCell className={"talkHeader whiteText boldText"}>{t('partner.tableLocationHeader')}</TableCell>
                        <TableCell className={"talkHeader whiteText boldText"}>{t('partner.tableCodeHeader')}</TableCell>
                        <TableCell className={"talkHeader whiteText boldText"}>{t('partner.tableInitDateHeader')}</TableCell>
                        <TableCell className={"talkHeader whiteText boldText"}>{t('partner.tableFinishDateHeader')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {talks?.talks.map((talk, index) => (
                        <TableRow
                            key={talk.talk}
                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            className={getRowClassName(index)}
                        >
                            <TableCell component="th" scope="row" className={"whiteText"}>{talk.talk}</TableCell>
                            <TableCell component="th" scope="row" className={"whiteText"}>{talk.location? talk.location : ""}</TableCell>
                            <TableCell component="th" scope="row" className={"whiteText"}>{talk.talkCode}</TableCell>
                            <TableCell className={"whiteText"}>{talk.talkStartTime && getFormatedDate(talk.talkStartTime)}</TableCell>
                            <TableCell className={"whiteText"}>{talk.talkFinishTime && getFormatedDate(talk.talkFinishTime)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <p className={"updateTalksMsg whiteText boldUnderlined"}>{t('partner.updateTalks')}</p>
            <p className={"whiteText"}>{t('partner.step1')}</p>
            <ul>
                <li>
                    <img src={excel} alt="Excel logo" className={"excel"}/>
                    <a href={require('../templates/spanish-template.xlsx')} className={"whiteText"}>{t('partner.esTemplate')}
                        <img src={es} alt="Spanish Languaje" className={"esflag"}/>
                    </a></li>
                <li>
                    <img src={excel} alt="Excel logo" className={"excel"}/>
                    <a href={require('../templates/english-template.xlsx')} className={"whiteText"}>{t('partner.enTemplate')}
                        <img src={en} alt="Enlgish Languaje" className={"esflag"}/>
                    </a></li>
            </ul>
            <p className={"whiteText"}>{t('partner.step2')}</p>
            <p className={"whiteText"}>{t('partner.step3')}</p>
            <input
                className={"whiteText"}
                type="file"
                aria-label={t('partner.uploadTalks')}
                ref={fileInputRef}
                onChange={fileInputHandler}
                />
            <img src={logo} alt="ProxyHands logo" className={"logo"}/>
        </TableContainer>
        </div>
        } else if (tokenValidated && talks?.event.event) {
            return <div className={"paddingLeft"}>
                <a href={process.env.REACT_APP_MAIN_URL + ""}><button className={"topRight"} onClick={logout}><LogoutIcon className={"logoutIcon"}/>{t('partner.exit')}</button></a>
                <h1 className={"whiteText textCenter"}>{talks?.event.event}</h1>
                <h3 className={"whiteText"}>{t('partner.noTalks')}</h3>
                <p className={"whiteText"}>{t('partner.step1')}</p>
                <ul>
                    <li>
                        <img src={excel} alt="Excel logo" className={"excel"}/>
                        <a href={require('../templates/spanish-template.xlsx')} className={"whiteText"}>{t('partner.esTemplate')}
                        <img src={es} alt="Spanish Languaje" className={"esflag"}/>
                    </a></li>
                    <li>
                        <img src={excel} alt="Excel logo" className={"excel"}/>
                        <a href={require('../templates/english-template.xlsx')} className={"whiteText"}>{t('partner.enTemplate')}
                        <img src={en} alt="Enlgish Languaje" className={"esflag"}/>
                    </a></li>
                </ul>
                <p className={"whiteText"}>{t('partner.step2')}</p>
                <p className={"whiteText"}>{t('partner.step3')}</p>
                <input
                    className={"whiteText"}
                    type="file"
                    aria-label={t('partner.uploadTalks')}
                    ref={fileInputRef}
                    onChange={fileInputHandler}
                />
                <img src={logo} alt="ProxyHands logo" className={"logo"}/>
            </div>
        } else return <ErrorPage />
    } else return null
}